import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import openHtmlInNewTab from '@/lib/openHtmlInNewTab';
// Phase 185.e — popup-blocker-proof signed view links for invoices
import openSignedDocInNewTab from '@/lib/openSignedDocInNewTab';
import { downloadDocument } from '@/lib/downloadDocument';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import {
  CreditCard, IndianRupee, Download, CheckCircle, Clock, AlertTriangle, Loader2,
  Eye, MoreHorizontal, FileText, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: Clock },
  paid: { label: 'Paid', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', icon: CheckCircle },
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: AlertTriangle },
  cancelled: { label: 'Cancelled', color: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400', icon: Clock },
};

export default function Subscription() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);
  const [mockPayDialog, setMockPayDialog] = useState(null);
  const [downloading, setDownloading] = useState(null);
  // Phase 254-EmailDeepLink — one-shot auto-arm guard so refreshing the page
  // doesn't re-launch Razorpay every time. Ref (NOT state) — purely a
  // run-once latch with no render dependency.
  const autoArmedRef = useRef(false);

  const fetchInvoices = useCallback(async () => {
    try {
      const res = await api.get('/subscription-invoices');
      setInvoices(res.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  // Load Razorpay script
  useEffect(() => {
    if (!document.getElementById('razorpay-script')) {
      const script = document.createElement('script');
      script.id = 'razorpay-script';
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      document.head.appendChild(script);
    }
  }, []);

  // Phase 254-EmailDeepLink — when the user lands here from an email/SMS
  // reminder link carrying `?invoice_id=<X>`, auto-launch the Razorpay
  // checkout for that exact invoice instead of forcing them to hunt the row.
  // Idempotent: only fires once per page-load (`autoArmedRef` latch) and
  // strips the query param immediately so a manual refresh can't re-launch.
  useEffect(() => {
    if (loading || invoices.length === 0) return;
    if (autoArmedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const targetId = params.get('invoice_id');
    if (!targetId) return;
    autoArmedRef.current = true;
    const target = invoices.find((i) => i.id === targetId);
    if (!target) return;
    // Strip the query param NOW so a mid-flow refresh can't double-fire.
    window.history.replaceState({}, '', window.location.pathname);
    if (target.status === 'paid') {
      toast.info(`Invoice ${target.custom_id || ''} is already paid.`);
      return;
    }
    if (target.status === 'cancelled') return;
    toast.info(`Launching payment for ${target.custom_id || 'invoice'}…`);
    handlePay(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, invoices]);

  const handlePay = async (invoice) => {
    if (invoice.status === 'paid' || invoice.status === 'cancelled') return;
    setPaying(invoice.id);

    try {
      // Create order via new unified endpoint
      const orderRes = await api.post('/subscription/create-order', { invoice_id: invoice.id });
      const order = orderRes.data;

      if (order.status === 'paid') {
        toast.success('Invoice already paid!');
        fetchInvoices();
        setPaying(null);
        return;
      }

      if (order.mock_mode) {
        // Show payment dialog only when platform Razorpay isn't yet configured (truly mock mode)
        setMockPayDialog({ invoice, order });
        setPaying(null);
        return;
      }

      // Real Razorpay checkout
      if (!window.Razorpay) {
        // Load Razorpay script dynamically
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        document.body.appendChild(script);
        await new Promise(resolve => { script.onload = resolve; setTimeout(resolve, 3000); });
      }

      if (!window.Razorpay) {
        toast.error('Razorpay SDK not loaded. Please refresh and try again.');
        setPaying(null);
        return;
      }

      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency || 'INR',
        order_id: order.order_id,
        name: order.academy_name || 'Out-Play Subscription',
        description: order.description || `Invoice ${order.invoice_custom_id}`,
        handler: async function (response) {
          try {
            const verifyRes = await api.post('/subscription/verify-payment', {
              invoice_id: invoice.id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            toast.success(verifyRes.data.message);
            fetchInvoices();
          } catch (err) {
            toast.error('Payment verification failed. Contact support.');
          }
          setPaying(null);
        },
        modal: {
          ondismiss: function () {
            setPaying(null);
            toast.info('Payment cancelled');
          }
        },
        prefill: {},
        theme: { color: '#10B981' }
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (response) {
        toast.error(`Payment failed: ${response.error.description}`);
        setPaying(null);
      });
      rzp.open();
      return;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create payment order');
    }
    setPaying(null);
  };

  const handleViewPdf = (invoice) => openSignedDocInNewTab(
    `/invoice/${invoice.id}/pdf`,
    { label: 'Invoice', errorMessage: 'Failed to load invoice' },
  );

  // Phase 185 — universal PDF/HTML fallback delegated to the shared helper.
  // The helper handles: PDF success, X-Pdf-Fallback HTML response (Phase 185
  // transparent fallback), AND defensive 5xx retry with ?format=html.

  const handleDownloadPdf = async (invoice) => {
    setDownloading(invoice.id);
    await downloadDocument(
      api,
      `/subscription-invoices/${invoice.id}/download`,
      `TaxInvoice_${invoice.custom_id || 'INVOICE'}.pdf`,
      { label: `Tax Invoice ${invoice.custom_id || ''}` }
    );
    setDownloading(null);
  };

  // Phase 68 — Digital payment receipt (paid invoices only)
  const handleDownloadReceipt = async (invoice) => {
    setDownloading(invoice.id);
    await downloadDocument(
      api,
      `/subscription-invoices/${invoice.id}/receipt/download`,
      `Receipt_${invoice.custom_id || 'INVOICE'}.pdf`,
      { label: `Receipt ${invoice.custom_id || ''}` }
    );
    setDownloading(null);
  };

  const paidInvoices = invoices.filter(i => i.status === 'paid');
  const pendingInvoices = invoices.filter(i => i.status === 'pending' || i.status === 'overdue');
  const paidAmount = paidInvoices.reduce((s, i) => s + (i.amount || 0), 0);
  const pendingAmount = pendingInvoices.reduce((s, i) => s + (i.amount || 0), 0);

  if (loading) return (
    <div className="space-y-6"><Skeleton className="h-8 w-48" /><div className="grid grid-cols-3 gap-4">{[1,2,3].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}</div><Skeleton className="h-64 rounded-xl" /></div>
  );

  return (
    <div className="space-y-6" data-testid="subscription-page">
      <div>
        <h1 className="text-2xl font-display font-semibold">Subscription & Billing</h1>
        <p className="text-sm text-muted-foreground">View and pay your Out-Play subscription invoices</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Total Paid</span>
              <CheckCircle className="h-4 w-4 text-emerald-600" />
            </div>
            <p className="text-2xl font-display font-semibold tabular-nums text-emerald-600">₹{paidAmount.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">{paidInvoices.length} invoice{paidInvoices.length !== 1 ? 's' : ''}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Pending</span>
              <AlertTriangle className="h-4 w-4 text-amber-500" />
            </div>
            <p className="text-2xl font-display font-semibold tabular-nums text-amber-600">₹{pendingAmount.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">{pendingInvoices.length} invoice{pendingInvoices.length !== 1 ? 's' : ''}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Plan</span>
              <CreditCard className="h-4 w-4 text-primary" />
            </div>
            <p className="text-2xl font-display font-semibold">Pro</p>
            <p className="text-xs text-muted-foreground">₹999/month + GST</p>
          </CardContent>
        </Card>
      </div>

      {/* Invoice Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Subscription Invoices</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-36">Invoice</TableHead>
                <TableHead className="hidden md:table-cell">Plan</TableHead>
                <TableHead className="hidden md:table-cell">Students</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead className="hidden md:table-cell">Due Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-32"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <div className="text-base font-medium text-foreground">No subscription invoices found</div>
                      <p className="text-xs max-w-md">If you're expecting an invoice and don't see it here, your account may not be linked to an academy. Sign out and sign back in to refresh your session. If the problem persists, email <a href="mailto:contact@out-play.in" className="text-primary hover:underline">contact@out-play.in</a>.</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : invoices.map((inv) => {
                const st = statusConfig[inv.status] || statusConfig.pending;
                const canPay = inv.status === 'pending' || inv.status === 'overdue';
                return (
                  <TableRow key={inv.id} data-testid={`invoice-row-${inv.custom_id}`}>
                    <TableCell>
                      <span className="text-xs font-mono font-medium text-primary">{inv.custom_id}</span>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <Badge variant="outline" className="text-xs capitalize">{inv.plan}</Badge>
                    </TableCell>
                    <TableCell className="hidden md:table-cell tabular-nums">{inv.student_count}</TableCell>
                    <TableCell className="font-semibold tabular-nums">₹{(inv.amount || 0).toLocaleString()}</TableCell>
                    <TableCell className="hidden md:table-cell text-sm">{inv.due_date}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${st.color}`}>
                        <st.icon className="h-3 w-3" /> {st.label}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {canPay && (
                          <Button
                            size="sm"
                            className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => handlePay(inv)}
                            disabled={paying === inv.id}
                            data-testid="subscription-pay-button"
                          >
                            {paying === inv.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><CreditCard className="h-3 w-3 mr-1" /> Pay Now</>}
                          </Button>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7">
                              <MoreHorizontal className="h-3.5 w-3.5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewPdf(inv)}>
                              <Eye className="h-4 w-4 mr-2" /> View Invoice
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDownloadPdf(inv)} disabled={downloading === inv.id} data-testid={`subscription-download-invoice-${inv.custom_id}`}>
                              {downloading === inv.id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                              Download Tax Invoice
                            </DropdownMenuItem>
                            {/* Phase 68 — Receipt download (paid invoices only) */}
                            {inv.status === 'paid' && (
                              <DropdownMenuItem onClick={() => handleDownloadReceipt(inv)} disabled={downloading === inv.id} data-testid={`subscription-download-receipt-${inv.custom_id}`}>
                                {downloading === inv.id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                                Download Receipt
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Mock Payment Dialog — shown ONLY when platform Razorpay isn't yet configured */}
      <Dialog open={!!mockPayDialog} onOpenChange={() => setMockPayDialog(null)}>
        <DialogContent className="max-w-sm" aria-describedby="mock-pay-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CreditCard className="h-5 w-5 text-primary" /> Complete Payment</DialogTitle>
          </DialogHeader>
          <p id="mock-pay-desc" className="sr-only">Manual payment confirmation when Razorpay gateway is not yet active.</p>
          {mockPayDialog && (
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 text-center">
                <p className="text-xs text-muted-foreground">Amount Due</p>
                <p className="text-3xl font-display font-bold text-emerald-700 mt-1">₹{(mockPayDialog.order.amount / 100).toLocaleString()}</p>
                <p className="text-xs text-muted-foreground mt-1">{mockPayDialog.order.invoice_custom_id} — Pro Plan</p>
              </div>
              {/* Phase 68.1 — Explicit Razorpay-not-configured banner so the user knows WHY no gateway opens */}
              <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
                <p className="text-xs text-amber-800 dark:text-amber-200 leading-relaxed">
                  <Shield className="h-3 w-3 inline mr-1 mb-0.5" />
                  <strong>Razorpay gateway not yet active</strong> — Out-Play's admin hasn't configured the payment provider yet. Clicking "Confirm Payment" below records this as a manual payment for now. Online card / UPI / Net Banking will be enabled shortly. Questions? <a href="mailto:contact@out-play.in" className="underline">contact@out-play.in</a>
                </p>
              </div>
              {mockPayDialog.order.fallback_reason && (
                <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <p className="text-xs text-slate-700">{mockPayDialog.order.fallback_reason}</p>
                </div>
              )}
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setMockPayDialog(null)}>Cancel</Button>
                <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={async () => {
                  setPaying(mockPayDialog.invoice.id);
                  try {
                    const res = await api.post('/subscription/verify-payment', {
                      invoice_id: mockPayDialog.invoice.id,
                      payment_id: `pay_${Date.now()}`,
                      order_id: mockPayDialog.order.order_id
                    });
                    toast.success(res.data.message);
                    setMockPayDialog(null);
                    fetchInvoices();
                  } catch (err) {
                    toast.error('Payment failed');
                  }
                  setPaying(null);
                }}>
                  {paying ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                  Confirm Payment (₹{(mockPayDialog.order.amount / 100).toLocaleString()})
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
